// ABOUTME: Card displaying today's recording progress per child toward daily behavior logging goals
// ABOUTME: Shows individual child progress bars with color coding and family total (Issue #833)

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Avatar
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { familyAPI, behaviorAPI } from '../services/api';
import { Child } from '../types/api';
import { statusTint } from '../theme/tokens';
import naluV1Theme from '../theme/theme.nalu-v1';

interface TodaysRecordingProgressCardProps {
  familyId: number;
  familyTimezone?: string;
}

interface ChildProgressData {
  child: Child;
  todaysCount: number;
  idealGoal: number;
  percentage: number;
  color: 'red' | 'yellow' | 'green';
}

interface ProgressCardData {
  children: ChildProgressData[];
  familyTotal: {
    totalCount: number;
    totalIdeal: number;
    percentage: number;
    color: 'red' | 'yellow' | 'green';
  };
  familyName: string;
}

// Helper function to determine color based on percentage
const getProgressColor = (percentage: number): 'red' | 'yellow' | 'green' => {
  if (percentage >= 100) return 'green';
  if (percentage >= 75) return 'yellow';
  return 'red';
};

const TodaysRecordingProgressCard: React.FC<TodaysRecordingProgressCardProps> = React.memo(({ 
  familyId, 
  familyTimezone 
}) => {
  const activeTheme = useTheme();
  const isNaluV1Theme = activeTheme.logoSrc === naluV1Theme.logoSrc;

  // Declared inside the component (not module scope) so it reads the family's active
  // theme rather than always resolving default-theme colors regardless of nalu-v1 (issue #1900).
  const getMUIColor = (color: 'red' | 'yellow' | 'green'): string => {
    switch (color) {
      case 'red': return activeTheme.palette.error.main;
      case 'yellow': return activeTheme.palette.warning.main;
      case 'green': return activeTheme.palette.success.main;
      default: return activeTheme.palette.error.main;
    }
  };
  const [progressData, setProgressData] = useState<ProgressCardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProgressData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Get family info, children, effective daily goal, and today's behavior counts
      const [family, children, effectiveGoal, behaviorCountsResponse] = await Promise.all([
        familyAPI.getFamilyDetails(familyId),
        familyAPI.getChildren(familyId),
        familyAPI.getEffectiveDailyLoggingGoal(familyId),
        behaviorAPI.getTodaysBehaviorCountsByChild(familyId, { 
          timezone: familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone 
        })
      ]);

      if (children.length === 0) {
        // Handle empty state
        setProgressData({
          children: [],
          familyTotal: { totalCount: 0, totalIdeal: 0, percentage: 0, color: 'red' },
          familyName: family?.name || 'Family'
        });
        return;
      }

      // Calculate progress for each child
      const childProgressData: ChildProgressData[] = children.map(child => {
        const childId = child.id || child.childId || 0;
        const todaysCount = behaviorCountsResponse[childId] || 0;
        const percentage = effectiveGoal > 0 ? Math.round((todaysCount / effectiveGoal) * 100) : 0;
        const color = getProgressColor(percentage);

        return {
          child,
          todaysCount,
          idealGoal: effectiveGoal,
          percentage,
          color
        };
      });

      // Calculate family total
      const totalCount = childProgressData.reduce((sum, child) => sum + child.todaysCount, 0);
      const totalIdeal = childProgressData.length * effectiveGoal;
      const familyPercentage = totalIdeal > 0 ? Math.round((totalCount / totalIdeal) * 100) : 0;
      const familyColor = getProgressColor(familyPercentage);

      setProgressData({
        children: childProgressData,
        familyTotal: {
          totalCount,
          totalIdeal,
          percentage: familyPercentage,
          color: familyColor
        },
        familyName: family?.name || 'Family'
      });

    } catch (error: unknown) {
      console.error('Failed to load progress data:', error);
      setError('Unable to load progress data');
    } finally {
      setLoading(false);
    }
  }, [familyId, familyTimezone]);

  useEffect(() => {
    loadProgressData();
  }, [loadProgressData]);

  if (loading) {
    return (
      <Card data-testid="todays-recording-progress-card" className="MuiCard-root">
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 120 }}>
            <CircularProgress data-testid="loading-spinner" />
            <Typography sx={{ ml: 2 }}>Loading...</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="error-state-card" className="MuiCard-root">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Recording Progress
          </Typography>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  if (!progressData || progressData.children.length === 0) {
    return (
      <Card data-testid="empty-state-card" className="MuiCard-root">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Recording Progress
          </Typography>
          <Typography color="text.secondary">
            No children in family yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Calculate chip color based on family total progress
  const getChipColor = (percentage: number): { bg: string; border: string } => {
    if (percentage >= 100) {
      return { bg: statusTint.successBg, border: activeTheme.palette.success.main };
    } else if (percentage >= 75) {
      return { bg: statusTint.warningBg, border: activeTheme.palette.warning.main };
    } else {
      return { bg: statusTint.errorBg, border: activeTheme.palette.error.main };
    }
  };

  const chipColors = getChipColor(progressData.familyTotal.percentage);

  return (
    <Card data-testid="todays-recording-progress-card" className="MuiCard-root" role="region" aria-label="Today's recording progress">
      <CardContent sx={{ pb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2.5 }}>
          Today&apos;s Recording Progress
        </Typography>

        {/* Target Chip - Issue #859 */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <Box
            data-testid="target-chip"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              backgroundColor: chipColors.bg,
              border: `2px solid ${chipColors.border}`,
              borderRadius: isNaluV1Theme ? '24px' : 2,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              fontWeight: 600,
            }}
          >
            Target: {progressData.familyTotal.totalIdeal}
          </Box>
        </Box>

        {/* Descriptive Text - Issue #859 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Target: {progressData.children[0]?.idealGoal || 0} {(progressData.children[0]?.idealGoal || 0) === 1 ? 'Behavior' : 'Behaviors'} Per Child Per Day
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mt: 0.5 }}>
            Today vs Target
          </Typography>
        </Box>

        {/* Individual child progress */}
        {progressData.children.map((childProgress, index) => {
          const childId = childProgress.child.id || childProgress.child.childId || index + 1;
          const progressBarWidth = Math.min(childProgress.percentage, 100);
          
          return (
            <Box key={childId} sx={{ mb: 1.5 }}>
              {/* Child name and count */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', maxWidth: '70%' }}>
                  <Avatar 
                    sx={{ 
                      width: 24, 
                      height: 24, 
                      fontSize: '0.75rem',
                      bgcolor: 'primary.main'
                    }}
                  >
                    {childProgress.child.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography
                    variant="body2"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    data-testid={`child-name-${childId}`}
                  >
                    {childProgress.child.name}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    minWidth: 'fit-content'
                  }}
                >
                  {childProgress.todaysCount}/{childProgress.idealGoal}
                </Typography>
              </Box>
              
              {/* Progress bar */}
              <Box sx={{ position: 'relative' }}>
                {/* Background bar (ideal goal) */}
                <Box
                  sx={{
                    height: 6,
                    backgroundColor: activeTheme.palette.grey[300],
                    borderRadius: isNaluV1Theme ? '36px' : 3,
                    overflow: 'hidden'
                  }}
                  data-testid={`progress-background-${childId}`}
                >
                  {/* Progress fill (only if count > 0) */}
                  {childProgress.todaysCount > 0 && (
                    <Box
                      sx={{
                        height: '100%',
                        width: `${progressBarWidth}%`,
                        backgroundColor: getMUIColor(childProgress.color),
                        borderRadius: isNaluV1Theme ? '36px' : 3,
                        transition: 'width 0.3s ease-in-out'
                      }}
                      data-testid={`progress-bar-${childId}`}
                      role="progressbar"
                      aria-label={`${childProgress.child.name} progress`}
                      aria-valuenow={childProgress.percentage}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      tabIndex={0}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}
        
        {/* Family total callout box */}
        <Box 
          sx={{ 
            mt: 2,
            p: 2,
            backgroundColor: progressData.familyTotal.percentage >= 100 ? statusTint.successBg : activeTheme.palette.grey[100],
            borderRadius: isNaluV1Theme ? '24px' : 2,
            border: 2,
            borderColor: progressData.familyTotal.percentage >= 100 ? activeTheme.palette.success.main : activeTheme.palette.grey[300],
            textAlign: 'center'
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: progressData.familyTotal.percentage >= 100 ? activeTheme.palette.success.dark : activeTheme.palette.text.primary,
              mb: 0.5
            }}
            data-testid="family-total-progress"
          >
            {progressData.familyName}: {progressData.familyTotal.totalCount}/{progressData.familyTotal.totalIdeal} behaviors logged
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontSize: '0.8125rem',
              color: progressData.familyTotal.percentage >= 100 ? activeTheme.palette.success.dark : activeTheme.palette.text.secondary,
              display: 'block'
            }}
          >
            You recorded {progressData.familyTotal.totalCount} behavior{progressData.familyTotal.totalCount !== 1 ? 's' : ''} today
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
});

// Add display name for debugging
TodaysRecordingProgressCard.displayName = 'TodaysRecordingProgressCard';

export default TodaysRecordingProgressCard;